import { FC, useRef, useState } from 'react'
import { ScrollSequenceProps } from 'components/ScrollSequence/types'
import styles from './ScrollSequence.module.scss'
import { useGSAP } from '@gsap/react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import cx from 'classnames'
import { AffirmLogo } from 'components/_const_assets/affirmLogo'
import { Button } from 'components/Phantom/Button'
import { Icon, IconName } from 'components/Phantom/Icon'
import { A } from 'components/basic/A'

const SCROLL_OFFSET = '-500px'
/**
 * This component is a demo of a scroll sequence animation.
 *
 * There's a lot of finesse to be gained here. Here are some thoughts:
 * - There's a lot to be played with the `scrub` parameter. I've got it set to true (0) right now, but it's kinda nice
 * to have it have inertia at like 1.
 * - I wanna make that reveal at the bottom as cool as possible.
 * @param props
 * @constructor
 */
export const ScrollSequence: FC<ScrollSequenceProps> = (props) => {
	const imgWrapper = useRef<HTMLDivElement>(null)
	const imageDimRef = useRef<HTMLImageElement>(null)
	const imageLightRef = useRef<HTMLImageElement>(null)
	const bottomContainerRef = useRef<HTMLDivElement>(null)
	const trolleyRef = useRef<HTMLDivElement>(null)

	const [showButton, setShowButton] = useState(false)

	useGSAP(() => {
		gsap.registerPlugin(ScrollTrigger)

		ScrollTrigger.config({
			ignoreMobileResize: true,
		})

		const timeline = gsap.timeline()
		timeline.fromTo(
			imgWrapper.current,
			{ scale: 1 },
			{
				scale: 1.5,
			}
		)

		timeline.to(
			imgWrapper.current,
			{
				y: SCROLL_OFFSET,
				ease: 'power2.in',
			},
			0
		)

		ScrollTrigger.create({
			trigger: trolleyRef.current,
			animation: timeline,
			start: '-200px top',
			end: `+${props.length}px`,
			pin: true,
			anticipatePin: 1,
			markers: props.markers,
			pinType: 'transform',
			// pinReparent: true,
			scrub: props.scrubSeconds,
			onLeave: () => {
				gsap.to([bottomContainerRef.current, imageLightRef.current], {
					opacity: 1,
				})

				setShowButton(true)
			},
			onEnterBack: () => {
				gsap.to([bottomContainerRef.current, imageLightRef.current], {
					opacity: 0,
				})

				setShowButton(false)
			},
		})
	}, [props.markers])

	return (
		<div className={styles.container}>
			<div className={styles.top_content}>
				<header className={styles.header}>
					<h2>{props.title}</h2>
					<p>{props.subtitle}</p>
				</header>
				<div className={styles.top_cta}>
					<div className={styles.cta_container}>
						<Button.White
							id={'scroll_cta'}
							href={'/product/pod-cover'}
						>
							Shop now
						</Button.White>
					</div>

					<p className={styles.affirm_text}>
						Pay over time with&nbsp;
						<span>
							<AffirmLogo textColor={'white'} />
						</span>
					</p>
				</div>
			</div>

			<div
				className={styles.image_container}
				ref={trolleyRef}
			>
				<div
					className={styles.image_wrapper}
					ref={imgWrapper}
				>
					<img
						src={'https://eightsleep.imgix.net/scrollsequence/hub-light-off.png'}
						className={styles.image}
						alt={''}
						ref={imageDimRef}
						aria-hidden={true}
					/>
					<img
						src={'https://eightsleep.imgix.net/scrollsequence/hub-light-on.png'}
						className={cx(styles.image, styles.image_hidden)}
						ref={imageLightRef}
						alt={''}
						aria-hidden={true}
					/>
				</div>
			</div>
			<div
				style={{ marginTop: SCROLL_OFFSET }}
				ref={bottomContainerRef}
				className={styles.bottom_container}
			>
				<A
					id="scroll_shop_now"
					href="/pod-cover"
					className={styles.button}
				>
					{/*<div*/}
					{/*	style={{*/}
					{/*		boxShadow: showButton ? 'black 0 0 20px 0 inset' : 'black 0 0 20px 100px inset',*/}
					{/*	}}*/}
					{/*/>*/}
					Shop now
				</A>
				<div className={styles.benefits_wrapper}>
					<ul className={styles.benefits}>
						{benefits.map((b, i) => (
							<Benefit
								key={i}
								title={b.title}
								description={b.description}
								icon={b.icon}
							/>
						))}
					</ul>
				</div>

				<div className={styles.bottom_text}>
					<p>
						We currently ship to the following regions. United States <em>(excluding Alaska, Hawaii, Puerto Rico and other US territories)</em>, European Union{' '}
						<em>(excluding Cyprus, Greece and Malta)</em> Canada, United Kingdom and Australia.
					</p>
				</div>
			</div>
		</div>
	)
}

interface BenefitProps {
	title: string
	description: string
	icon: IconName
}

const benefits: BenefitProps[] = [
	{
		icon: 'MoonLight',
		title: 'Try risk-free for 30 nights',
		description: 'Enjoy the Pod at home, and return it for a full refund if you don’t love it',
	},
	{
		icon: 'DeliveryLight',
		title: 'Free shipping & returns',
		description: 'Free shipping in nearly 30 countries with no-cost, hassle-free returns',
	},
	{
		icon: 'WarrantyLight',
		title: 'Worry-free warranty',
		description: 'Ongoing Pod protection for up to 5 years. Warranty terms apply',
	},
]

const Benefit: FC<{ title: string; description: string; icon: IconName }> = (props) => {
	return (
		<li className={styles.benefit}>
			<Icon
				name={props.icon}
				color={'white'}
				size={32}
			/>
			<h3 className={styles.title}>{props.title}</h3>
			<p className={styles.description}>{props.description}</p>
		</li>
	)
}
